import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-01-24 01:08:33 +0100 (Sat, 24 Jan 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159987 $
// Purpose     : low level react components
// =============================================================================
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
// ---Framework imports
import { map, reversed, size, cloneDeep } from 'ldash';
import { Sel, Actions } from 'uix/registries';
import connect from 'uix/connect';
import styles from 'uix/styles';
import { useState, useEffect, useInView, useSelector } from 'uix/hooks';
import { useWebcacheWidth } from 'uix/hooks/webcache';
import { hashObj } from 'uix/utils';
import log from 'uix/logging';
// ---Local imports
import navbarStyle from './verticalNavbar.scss';
import S from './index.scss';
import { Button } from './button';
// ---Exports
export { Link, UnknownDomainLink, ApiLink } from './links';
export { GlobalMenu, ConfirmButton, XMenu, Confirmation, DDMenu, ProgressMenuItem } from './menu';
export { Toolbar } from './toolbar';
export { ContextMenu } from './contextMenu';
export { createPortal, Portal, AutoPortal, PortalTarget } from './portals';
export { XPopup } from './popup';
export { DatePicker, DayPicker } from './datepicker';
export { PieChart, BarChart, AreaChart } from './charts';
export { Editor, Differ } from './editor';
export { XTab } from './tabs';
export { Button, ButtonGroup } from './button';
export { Tiles, Tile } from './tiles';
export { Modal } from './modal';
export { HeaderSettingsIcon, HeaderSegment, TableSegment } from './header';
export { TranslatedTable, SelectableTable, useTreeCellRenderer } from './table';
export { Dialog, ResizableDialog, TransitionDialog } from './dialog';
export { Dropper } from './dropper';
export { DelayedDisplay } from './delayedDisplay';
export { GlobalKeyboardShortcuts, KeyboardShortCut } from './keyboardShortcuts';
export { VIcon } from 'uix/graphics';
export { SplitPane } from './splitpane';
export { RGrid, RGridNonStacking } from './grid';
export { default as AutoSizer } from './autosizer/AutoSizer';
export { ApiErrorMessage, ErrorBoundary } from './error';
export { SelectSome } from './select_some';
export { DebugFeature } from './debug';
export { MultiRangeSlider } from './multiRangeSlider.js';
export { ViewLoader } from './viewLoader';
export const LargeThumb = connect('LargeThumb', 'config.apiurl', ({ apiurl, pageid, onClick, pageRatio, width, link })=>{
    let url = `${apiurl}/static/graphics/error/noImageSuppliedThumbTransparent.svg`;
    let w = 304;
    if (pageid) {
        url = `${apiurl}/download/webcache/${w}/${pageid}`;
    }
    let props = {};
    if (onClick) {
        props.onClick = onClick;
        props.style = {
            cursor: 'pointer'
        };
    }
    let style = {};
    if (width) {
        style.minWidth = width;
        style.maxWidth = width;
    }
    if (pageRatio) {
        // 80px is the width of sui css .image.tiny
        style.minHeight = 80 * pageRatio;
    }
    return /*#__PURE__*/ _jsx("a", {
        className: " thumb",
        loading: "lazy",
        ...props,
        ...link,
        children: /*#__PURE__*/ _jsx("img", {
            style: {
                height: 'auto',
                display: 'block',
                backgroundColor: pageid ? '#EDD' : '#CCC',
                ...style,
                w
            },
            src: url
        })
    });
});
export const LoremBuilder = ({ small = false })=>{
    return /*#__PURE__*/ _jsxs("div", {
        className: "loremIpsum",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean venenatis ligula in odio consequat, ut vehicula dolor mollis. Sed suscipit ac magna porttitor scelerisque. Maecenas accumsan rhoncus ligula, ac imperdiet ipsum congue congue. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Morbi posuere odio felis, eu suscipit quam tincidunt eget. Vivamus ac ipsum quam. Morbi eu bibendum sapien. Maecenas vitae euismod metus, et congue urna. Sed maximus finibus quam. Morbi luctus, nisi sed laoreet elementum, urna odio fringilla libero, scelerisque scelerisque nisi odio vel diam. Nulla molestie egestas sodales. Morbi at faucibus neque."
            }),
            !small ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Curabitur semper elementum velit, vel porta justo porttitor eget. Nullam est nisi, mollis sit amet orci quis, tempus accumsan lacus. Sed placerat mi sed leo eleifend luctus. Donec vel erat a dolor posuere bibendum. Quisque convallis enim ut magna laoreet aliquet vel quis libero. In hac habitasse platea dictumst. Nunc consequat tellus sed dolor rutrum, a tristique ipsum imperdiet."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Duis ac ornare dui. Integer suscipit tortor pretium, accumsan diam nec, ultricies nisi. Maecenas sollicitudin nunc nisi, sed posuere felis vehicula a. Fusce sollicitudin eget lacus nec rhoncus. Nam est nisl, vestibulum non vehicula id, porttitor ut ipsum. In dignissim non tellus vehicula faucibus. Integer imperdiet massa vitae enim tristique interdum. Maecenas vulputate ipsum sit amet mi pretium pulvinar. Sed mauris risus, aliquet vitae ex sed, auctor porta nulla. Donec rutrum neque at fermentum accumsan. Duis at consequat leo, sit amet blandit nibh. Integer eget varius enim. Donec ullamcorper elit turpis, vel finibus nibh semper at. Etiam accumsan mollis finibus."
                    })
                ]
            }) : null
        ]
    });
};
export const AccessWarning = ({ children, awstate, id, onClick, zoom, style })=>{
    const [revealed, setRevealed] = useState(false);
    /* This component displays a warning overlay for sensitive content. It allows the user to reveal
 the content by clicking on the overlay or a button, depending on the provided props. */ useEffect(()=>{
        setRevealed(!awstate);
    }, [
        awstate,
        id
    ]);
    if (!awstate) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    let onClickHandler = ()=>{
        setRevealed(true);
        onClick?.();
    };
    let transformStyle = zoom ? {
        transform: `scale(${zoom / 100})`,
        transformOrigin: 'top left',
        width: `${100 / (zoom / 100)}%`,
        height: `${100 / (zoom / 100)}%`
    } : {};
    const accessWarningText = !revealed ? /*#__PURE__*/ _jsxs("div", {
        className: S.sensitiveOverlay,
        style: transformStyle,
        children: [
            "Dieses Bild enthält sensible Inhalte, die für Betrachter:innen verstörend sein könnten.",
            !onClick ? /*#__PURE__*/ _jsx(Button, {
                size: "small",
                inverted: true,
                bordered: true,
                content: "Klicken um anzuzeigen",
                circle: false,
                onClick: onClickHandler
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx("br", {}),
                    "Klicken um anzuzeigen"
                ]
            })
        ]
    }) : null;
    return /*#__PURE__*/ _jsxs("div", {
        style: style,
        onClick: onClick && onClickHandler,
        className: S.accessWariningCnt,
        children: [
            accessWarningText,
            /*#__PURE__*/ _jsx("div", {
                id: "AccessWarning",
                className: clsx(S.accessWarning, !revealed && S.blured),
                children: children
            })
        ]
    });
};
export const VerticalNavbar = ({ items, onClick })=>{
    let divs = items.map((item, i)=>/*#__PURE__*/ _jsxs("div", {
            className: clsx(navbarStyle.item, styles.cut, navbarStyle['itemAmount' + items.length]),
            onClick: ()=>onClick ? onClick(item) : null,
            children: [
                item.icon,
                item.text
            ]
        }, item.key || i));
    return /*#__PURE__*/ _jsx("div", {
        className: navbarStyle.verticalNavbar,
        children: divs
    });
};
export const InView = ({ children, flex = false, placeholder })=>{
    /* only render children if in visible screen position
   */ let [ref, onScreen] = useInView({
        triggerOnce: true
    });
    let style = flex ? {
        flex: 1
    } : undefined;
    if (!onScreen) {
        return /*#__PURE__*/ _jsx("div", {
            ref: ref,
            style: style,
            children: placeholder
        });
    } else {
        return children;
    }
};
export const WebcacheThumb = ({ otid, width = 128, pageRatio, page, link = false, style = {}, onClick, bboxes })=>{
    let apiurl = useSelector(Sel.apiurl);
    if (link) {
        style = {
            ...style,
            cursor: 'pointer'
        };
    }
    if (page) {
        pageRatio = page.height / page.width;
    }
    if (pageRatio) {
        style = {
            ...style,
            height: width * pageRatio
        };
    }
    let wcsize = useWebcacheWidth(width);
    let url = apiurl.href(`/download/webcache/${wcsize}/${otid}?orig=true`);
    let img = /*#__PURE__*/ _jsx("img", {
        loading: "lazy",
        style: {
            width,
            ...style
        },
        src: url,
        onClick: onClick
    });
    if (bboxes && page) {
        // annotate image with colored spans
        let overlays = bboxes.map((box, idx)=>{
            let [x1, y1, x2, y2] = [
                box[0] / page.width * 100,
                box[1] / page.height * 100,
                box[2] / page.width * 100,
                box[3] / page.height * 100
            ];
            let overlayStyle = {
                left: `${x1}%`,
                top: `${y1}%`,
                width: `${x2 - x1}%`,
                height: `${y2 - y1}%`,
                position: 'absolute',
                //~ background: 'rgba(255, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 255, 0.6)',
                pointerEvents: 'none'
            };
            return /*#__PURE__*/ _jsx("div", {
                style: overlayStyle
            }, idx);
        });
        img = /*#__PURE__*/ _jsxs("div", {
            style: {
                position: 'relative',
                width,
                height: style.height
            },
            children: [
                img,
                overlays
            ]
        });
    }
    return img;
};
